/**
 * OrderHoldSettingsSection — per-location "order editing hold" toggle.
 *
 * The hold is an editing window that keeps a brand-new order out of batching so
 * a customer can still edit or cancel it in Shopify. Turning it OFF for a
 * location makes new orders at that location batchable immediately. Default is
 * ON, with a 2-hour default window.
 *
 * BLU-718: this switch is the FLOOR's fallback. A client org that sets its own
 * `organizations.order_hold_minutes` (their Settings → Integrations → Order
 * editing window) OVERRIDES this switch for its own orders — the client is the
 * party handling customer edits. This toggle still governs every order whose
 * owning org has not set a window of its own.
 *
 * NOTE: the toggle affects NEW orders only — existing holds (`held_until`
 * already stamped on in-flight orders) are untouched.
 *
 * Mirrors ShippingProviderSettingsSection's per-location structure (embedded +
 * locationId props, getLocationSettings query, per-row mutate with a
 * variables-keyed pending spinner, toast + refetch) but renders a Switch per
 * location instead of a Select.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { MapPin, Loader2, AlertTriangle, RefreshCw, Clock, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface OrderHoldSettingsSectionProps {
  /** When true, renders without the Card wrapper for embedding in the Console. */
  embedded?: boolean;
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name (the Console section header already
   * names the location). Absent = full-list behavior.
   */
  locationId?: string;
}

export function OrderHoldSettingsSection({
  embedded = false,
  locationId,
}: OrderHoldSettingsSectionProps) {
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();

  const setOrderHoldMutation = trpc.productionSettings.setLocationOrderHold.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(variables.enabled ? 'Order editing hold enabled' : 'Order editing hold disabled');
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update order hold: ${error.message}`);
    },
  });

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Hold new orders before they can be batched, so a customer can still edit or cancel in
        Shopify. Turn off to make new orders at this location batchable immediately. A client who
        sets their own editing window overrides this for their own orders.
      </p>

      {/* Error State */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || 'Unknown error'}
            <Button variant="link" className="p-0 h-auto" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">Loading locations...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
          <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="font-medium">No locations configured</p>
          <p className="text-sm text-muted-foreground mb-4">
            Add locations in IMS Settings to configure the order hold
          </p>
          <Button variant="outline" asChild className="h-12 gap-2">
            <a href="/settings?tab=locations">
              <ExternalLink className="h-4 w-4" />
              Go to IMS Settings
            </a>
          </Button>
        </div>
      )}

      {/* Location List */}
      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location order editing hold">
          {locations.map((location) => {
            const enabled = location.orderHoldEnabled;
            const rowPending =
              setOrderHoldMutation.isPending &&
              setOrderHoldMutation.variables?.locationId === location.id;
            const switchId = `order-hold-${location.id}`;
            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                <div className="flex items-center justify-between p-3 transition-colors hover:bg-muted/50">
                  <div>
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={Clock} className="mt-1">
                      {enabled ? 'Holding new orders before batching' : 'Batchable immediately'}
                    </NoteLine>
                  </div>

                  <div className="flex items-center gap-2">
                    {rowPending && (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                    )}
                    <Label htmlFor={switchId} className="text-xs font-normal text-muted-foreground">
                      Order editing hold
                    </Label>
                    <Switch
                      id={switchId}
                      checked={enabled}
                      onCheckedChange={(next) =>
                        setOrderHoldMutation.mutate({ locationId: location.id, enabled: next })
                      }
                      disabled={rowPending}
                      aria-label={`Order editing hold for ${location.name}`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info about IMS locations */}
      {!isLoading && !hasError && locations.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">
          To add or remove locations, go to{' '}
          <a href="/settings?tab=locations" className="underline hover:text-foreground">
            IMS Settings - Locations
          </a>
        </p>
      )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {setOrderHoldMutation.isSuccess && 'Order editing hold updated'}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Order editing hold</CardTitle>
        </div>
        <CardDescription>
          Hold new orders briefly so customers can still edit or cancel in Shopify.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
